/**
 * Persisted "which sections are minimized" state for the todo-lists arche.
 *
 * Collapse/expand is a per-user, per-browser view preference — the same place
 * the platform's other internal arches keep their UI prefs (localStorage), not
 * Vault data. Persisting it here makes a minimized section stay minimized across
 * route changes within the SPA and across a full page refresh, without a server
 * round-trip.
 *
 * Shape: we store the set of *collapsed* section ids only. A section the user has
 * never touched is absent from the set and therefore expanded — matching the
 * historical `useState(true)` default and keeping the persisted record tiny.
 *
 * A single module-level singleton (surfaced through `useSyncExternalStore`) means
 * every `SectionCard` / board column shares one consistent view: toggling a
 * section in one place re-renders every subscriber, and there is exactly one
 * localStorage write per change.
 *
 * Storage failures degrade silently to in-session-only state — Safari private
 * mode rejects `setItem` with a quota error and there is no `window` under SSR;
 * neither should break the board.
 */
import { useCallback, useSyncExternalStore } from 'react';

/** Bump the `:vN` suffix if the persisted shape ever changes; the old key is
 *  orphaned (localStorage quota is in MB, a stale record costs nothing). */
const STORAGE_KEY = 'todo-lists:collapsed-sections:v1';

function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function readFromStorage(): Set<string> {
  const storage = safeLocalStorage();
  if (!storage) return new Set();
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    // Storage is mutable from devtools and from a future schema bump — never
    // trust the parsed JSON; keep only the string ids we understand.
    return new Set(parsed.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set();
  }
}

function writeToStorage(ids: Set<string>): void {
  const storage = safeLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // quota / private-mode — persistence degrades to in-session only
  }
}

// Module-level singleton: the single source of truth once loaded. `snapshot`
// only ever changes reference in `commit`, so `getSnapshot` is referentially
// stable between renders (required by useSyncExternalStore).
let snapshot: Set<string> | null = null;
const listeners = new Set<() => void>();

function getSnapshot(): Set<string> {
  if (snapshot === null) snapshot = readFromStorage();
  return snapshot;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function commit(next: Set<string>): void {
  snapshot = next;
  writeToStorage(next);
  listeners.forEach((l) => l());
}

/**
 * Collapse (`true`) or expand (`false`) one section by id, persisting the change.
 * Idempotent: a write that matches the current state is a no-op, so it never
 * spuriously re-renders subscribers or touches localStorage.
 */
export function setSectionCollapsed(sectionId: string, collapsed: boolean): void {
  const current = getSnapshot();
  if (collapsed === current.has(sectionId)) return;
  const next = new Set(current);
  if (collapsed) next.add(sectionId);
  else next.delete(sectionId);
  commit(next);
}

export interface SectionCollapseHandle {
  /** True when this section is minimized. */
  collapsed: boolean;
  /** Convenience inverse of {@link collapsed} — sections default to open. */
  expanded: boolean;
  /** Flip between collapsed and expanded. */
  toggle: () => void;
  /** Force a specific state (e.g. auto-expand when adding an item). */
  setCollapsed: (collapsed: boolean) => void;
}

/**
 * Subscribe a component to the persisted collapse state for one section.
 *
 * `sectionId` is any stable identity for the collapsible group — a real section
 * id for list/board sections, or a synthetic key (e.g. `personal-unsectioned:<listId>`)
 * for the board's section-less column. `expanded` is `true` for a section the
 * user has never minimized.
 */
export function useSectionCollapse(sectionId: string): SectionCollapseHandle {
  const ids = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const collapsed = ids.has(sectionId);
  const toggle = useCallback(
    () => setSectionCollapsed(sectionId, !getSnapshot().has(sectionId)),
    [sectionId],
  );
  const setCollapsed = useCallback(
    (value: boolean) => setSectionCollapsed(sectionId, value),
    [sectionId],
  );
  return { collapsed, expanded: !collapsed, toggle, setCollapsed };
}

/** Test-only: drop the in-memory singleton so each test starts from storage. */
export function __resetCollapsedSectionsForTests(): void {
  snapshot = null;
  listeners.clear();
}
